import React, { useState } from 'react';
import { Link } from 'react-router-dom';
import { 
  Mountain, 
  Compass, 
  Map as MapIcon, 
  Landmark,
  Trees,
  Waves,
  Sunset,
  Sparkles,
  Target,
  Users,
  Trophy,
  BookOpen,
  Zap,
  Rocket,
  ArrowRight,
  ChevronRight,
  Star
} from 'lucide-react';

interface Region {
  id: string;
  name: string;
  tagline: string;
  description: string;
  icon: any;
  color: string;
  bgColor: string;
  borderColor: string;
  position: { x: number; y: number };
  size: 'small' | 'medium' | 'large';
  capabilities: string[];
  frameworks: Array<{ name: string; id: string }>;
  terrain: string;
  discovered: boolean;
}

export const IslandExplorer: React.FC = () => {
  const [selectedRegion, setSelectedRegion] = useState<string | null>(null);
  const [visitedRegions, setVisitedRegions] = useState<Set<string>>(new Set());
  const [showMap, setShowMap] = useState(true);

  const regions: Region[] = [
    {
      id: 'clarity-peak',
      name: 'Clarity Peak',
      tagline: 'Where Vision Becomes Clear',
      description: 'Ascend to the highest point where strategic vision crystallizes. Here, leaders gain the altitude to see patterns invisible from the valley below.',
      icon: Mountain,
      color: 'text-consulting-slate-blue-700',
      bgColor: 'bg-consulting-slate-blue-50',
      borderColor: 'border-consulting-slate-blue-400',
      position: { x: 50, y: 15 },
      size: 'large',
      capabilities: ['Strategic Thinking', 'Vision Articulation', 'Pattern Recognition'],
      frameworks: [
        { name: 'Four-Level Diagramming', id: 'gf.diagram.levels' },
        { name: 'Relevance Framework', id: 'gf.relevance.framework' }
      ],
      terrain: 'Mountain peak with observation decks',
      discovered: true
    },
    {
      id: 'degap-canyon',
      name: 'DEGAP Canyon',
      tagline: 'Navigate the Path Between As-Is and To-Be',
      description: 'A five-stage journey through the canyon that separates current state from desired future. Each level reveals deeper insights about transformation.',
      icon: Landmark,
      color: 'text-[var(--mustard)]',
      bgColor: 'bg-[var(--mustard-dim)]',
      borderColor: 'border-[var(--mustard)]',
      position: { x: 30, y: 40 },
      size: 'large',
      capabilities: ['Gap Analysis', 'Change Management', 'Transformation Design'],
      frameworks: [
        { name: 'Five Stages of DEGAP', id: 'pf.degap.stages' }
      ],
      terrain: 'Five-tiered canyon with bridges',
      discovered: true
    },
    {
      id: 'priority-forest',
      name: 'Priority Forest',
      tagline: 'Where Decisions Take Root',
      description: 'Six ancient groves, each representing a domain of priorities. Navigate the paths between them to understand how priorities interconnect.',
      icon: Trees,
      color: 'text-sage-700',
      bgColor: 'bg-sage-50',
      borderColor: 'border-sage-400',
      position: { x: 65, y: 50 },
      size: 'medium',
      capabilities: ['Priority Assessment', 'Domain Expertise', 'Decision Framework'],
      frameworks: [
        { name: 'Six Kinds of Items', id: 'pf.items.kinds' }
      ],
      terrain: 'Six interconnected groves',
      discovered: false
    },
    {
      id: 'ritual-springs',
      name: 'Ritual Springs',
      tagline: 'Daily Practices Flow Naturally',
      description: 'Natural hot springs where rhythm and ritual restore clarity. Build sustainable habits in these rejuvenating waters.',
      icon: Waves,
      color: 'text-sky',
      bgColor: 'bg-[var(--slate-dim)]',
      borderColor: 'border-[var(--slate)]',
      position: { x: 20, y: 65 },
      size: 'small',
      capabilities: ['Rhythm & Ritual', 'Habit Formation', 'Process Discipline'],
      frameworks: [
        { name: 'Rhythm and Ritual', id: 'pf.cap.rhythm' }
      ],
      terrain: 'Terraced hot springs',
      discovered: false
    },
    {
      id: 'stakeholder-bay',
      name: 'Stakeholder Bay',
      tagline: 'Navigate Relationships with Intelligence',
      description: 'A protected harbor where diverse stakeholders gather. Master the tides of influence and alignment in these collaborative waters.',
      icon: Users,
      color: 'text-mint',
      bgColor: 'bg-[var(--green-dim)]',
      borderColor: 'border-[var(--green)]',
      position: { x: 75, y: 75 },
      size: 'medium',
      capabilities: ['Stakeholder Intelligence', 'Alignment', 'Collaboration'],
      frameworks: [
        { name: 'Stakeholder Intelligence', id: 'pf.cap.stakeholder' }
      ],
      terrain: 'Natural harbor with docks',
      discovered: false
    },
    {
      id: 'tradeoff-plateau',
      name: 'Tradeoff Plateau',
      tagline: 'Master the Art of Intelligent Choice',
      description: 'A vast elevated plain where competing priorities are weighed with precision. Learn to see the hidden costs of every decision.',
      icon: Target,
      color: 'text-coral-700',
      bgColor: 'bg-coral-50',
      borderColor: 'border-coral-400',
      position: { x: 45, y: 70 },
      size: 'large',
      capabilities: ['Tradeoff Intelligence', 'Cost-Benefit Analysis', 'Decision Quality'],
      frameworks: [
        { name: 'Tradeoff Intelligence', id: 'pf.cap.tradeoff' }
      ],
      terrain: 'Balanced scales landmark',
      discovered: false
    },
    {
      id: 'activation-lighthouse',
      name: 'Activation Lighthouse',
      tagline: 'From Knowledge to Action',
      description: 'A beacon that transforms understanding into movement. The four-layer architecture guides knowledge from concept to reality.',
      icon: Sunset,
      color: 'text-gold',
      bgColor: 'bg-[var(--mustard-dim)]',
      borderColor: 'border-[var(--mustard)]',
      position: { x: 85, y: 35 },
      size: 'medium',
      capabilities: ['Knowledge Activation', 'Implementation', 'Execution'],
      frameworks: [
        { name: 'Four-Layer Activation', id: 'act.layers' }
      ],
      terrain: 'Lighthouse on promontory',
      discovered: false
    }
  ];

  const handleRegionClick = (regionId: string) => {
    setSelectedRegion(selectedRegion === regionId ? null : regionId);
    setVisitedRegions(prev => new Set(prev).add(regionId));
  };

  const selectedRegionData = regions.find(r => r.id === selectedRegion);
  const progress = (visitedRegions.size / regions.length) * 100;

  return (
    <div className="mx-auto space-y-6">
      {/* NEW: Dimensional Spaces Preview */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-600 to-sage-600 text-white p-6 shadow-xl">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <h2 className="text-2xl font-bold mb-2 flex items-center gap-2">
              <Sparkles className="h-6 w-6" />
              New: Rich Dimensional Exploration
            </h2>
            <p className="text-white/90 mb-4 max-w-2xl">
              Experience a completely reimagined way to explore frameworks - navigate actual geographic spaces, 
              discover landmarks, and follow paths through knowledge terrain.
            </p>
            <Link
              to="/elevation/island/stakeholder-engagement"
              className="inline-flex items-center gap-2 px-6 py-3 bg-[var(--bg-raised)] text-consulting-slate-blue-600 font-bold hover:bg-consulting-slate-blue-50 transition-colors shadow-lg"
            >
              <MapIcon className="h-5 w-5" />
              Explore Stakeholder Engagement Island (Beta)
              <ArrowRight className="h-5 w-5" />
            </Link>
          </div>
        </div>
      </div>

      {/* Header */}
      <div className="bg-gradient-to-r from-consulting-slate-blue-50 via-sage-50 to-consulting-slate-blue-100 p-8 border-2 border-consulting-slate-blue-300">
        <div className="flex items-start justify-between">
          <div className="flex-1">
            <div className="flex items-center gap-3 mb-3">
              <MapIcon className="h-10 w-10 text-consulting-slate-blue-600" />
              <div>
                <h1 className="text-4xl font-display font-bold text-charcoal-900">Peak Priorities Island (Classic View)</h1>
                <p className="text-lg text-charcoal-600 mt-1">An Interactive Journey to Mastery</p>
              </div>
            </div>
            <p className="text-charcoal-700 max-w-3xl leading-relaxed">
              Explore a living geography where each region represents a crucial capability domain. 
              Journey through mountains, forests, and bays to discover the frameworks and tools that will elevate your leadership.
            </p>
          </div>
          <button
            onClick={() => setShowMap(!showMap)}
            className="ml-4 px-4 py-2 bg-consulting-slate-blue-600 hover:bg-consulting-slate-blue-700 text-white transition-colors flex items-center gap-2"
          >
            <Compass className="h-5 w-5" />
            {showMap ? 'Hide Map' : 'Show Map'}
          </button>
        </div>

        {/* Progress */}
        <div className="mt-6 bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
          <div className="flex items-center justify-between mb-2">
            <div className="flex items-center gap-2">
              <Trophy className="h-5 w-5 text-[var(--mustard)]" />
              <span className="font-semibold text-charcoal-900">Exploration Progress</span>
            </div>
            <span className="text-sm text-charcoal-600">
              {visitedRegions.size} of {regions.length} regions discovered
            </span>
          </div>
          <div className="w-full bg-[var(--hover-tint)] rounded-full h-3 overflow-hidden">
            <div 
              className="bg-gradient-to-r from-consulting-slate-blue-500 to-sage-500 h-full transition-all duration-500 rounded-full"
              style={{ width: `${progress}%` }}
            />
          </div>
        </div>
      </div>

      {/* The Island Map */}
      {showMap && (
        <div className="bg-[var(--bg-raised)] shadow-lg border-2 border-charcoal-200 overflow-hidden">
          <div className="bg-gradient-to-br from-sky to-consulting-slate-blue-100 p-8 relative" style={{ minHeight: '600px' }}>
            {/* Ocean background with gradient */}
            <div className="absolute inset-0 opacity-20">
              <div className="absolute inset-0 bg-gradient-radial from-transparent via-consulting-slate-blue-200 to-consulting-slate-blue-400" />
            </div>

            {/* Island landmass */}
            <div className="relative w-full h-full">
              <svg className="absolute inset-0 w-full h-full" style={{ zIndex: 0 }}>
                {/* Island outline - organic shape */}
                <path
                  d="M 150,100 Q 200,80 300,120 T 500,150 Q 600,180 650,250 T 680,400 Q 650,500 580,550 T 400,580 Q 250,570 150,500 T 120,350 Q 130,250 150,100 Z"
                  fill="rgba(140, 178, 153, 0.3)"
                  stroke="rgba(82, 107, 92, 0.5)"
                  strokeWidth="2"
                />
                {/* Terrain contours */}
                <path
                  d="M 200,150 Q 250,130 350,160 T 550,200 Q 600,230 630,300"
                  fill="none"
                  stroke="rgba(82, 107, 92, 0.2)"
                  strokeWidth="1"
                  strokeDasharray="5,5"
                />
              </svg>

              {/* Regions as interactive points */}
              {regions.map((region) => {
                const Icon = region.icon;
                const isVisited = visitedRegions.has(region.id);
                const isSelected = selectedRegion === region.id;
                const sizeClass = 
                  region.size === 'large' ? 'w-24 h-24' :
                  region.size === 'medium' ? 'w-20 h-20' :
                  'w-16 h-16';

                return (
                  <div
                    key={region.id}
                    className="absolute transform -translate-x-1/2 -translate-y-1/2 cursor-pointer group"
                    style={{ 
                      left: `${region.position.x}%`, 
                      top: `${region.position.y}%`,
                      zIndex: isSelected ? 20 : 10
                    }}
                    onClick={() => handleRegionClick(region.id)}
                  >
                    {/* Pulse animation for undiscovered regions */}
                    {!isVisited && (
                      <div className={`absolute inset-0 ${region.bgColor} rounded-full animate-ping opacity-30`} />
                    )}

                    {/* Region marker */}
                    <div className={`
                      ${sizeClass} ${region.bgColor} ${region.borderColor}
                      rounded-full border-4 flex items-center justify-center
                      transition-all duration-300 shadow-lg
                      ${isSelected ? 'scale-125 shadow-2xl ring-4 ring-consulting-slate-blue-300' : 'hover:scale-110'}
                      ${isVisited ? 'opacity-100' : 'opacity-75'}
                    `}>
                      <Icon className={`${region.color} ${region.size === 'large' ? 'h-10 w-10' : 'h-8 w-8'}`} />
                    </div>

                    {/* Region name tooltip */}
                    <div className={`
                      absolute top-full mt-2 left-1/2 transform -translate-x-1/2
                      bg-[var(--bg-raised)] px-3 py-1  shadow-lg border-2 ${region.borderColor}
                      whitespace-nowrap text-sm font-semibold ${region.color}
                      ${isSelected ? 'opacity-100' : 'opacity-0 group-hover:opacity-100'}
                      transition-opacity pointer-events-none
                    `}>
                      {region.name}
                      {isVisited && <Star className="inline h-3 w-3 text-[var(--mustard)] ml-1" />}
                    </div>

                    {/* Discovery badge */}
                    {isVisited && !region.discovered && (
                      <div className="absolute -top-2 -right-2 w-6 h-6 bg-[var(--mustard)] rounded-full flex items-center justify-center animate-bounce">
                        <Sparkles className="h-4 w-4 text-white" />
                      </div>
                    )}
                  </div>
                );
              })}

              {/* Path connections - visible after discovering regions */}
              {visitedRegions.size > 1 && (
                <svg className="absolute inset-0 w-full h-full pointer-events-none" style={{ zIndex: 5 }}>
                  {Array.from(visitedRegions).slice(0, -1).map((regionId, idx) => {
                    const region = regions.find(r => r.id === regionId);
                    const nextRegionId = Array.from(visitedRegions)[idx + 1];
                    const nextRegion = regions.find(r => r.id === nextRegionId);
                    
                    if (!region || !nextRegion) return null;

                    const startX = (region.position.x / 100) * 800;
                    const startY = (region.position.y / 100) * 600;
                    const endX = (nextRegion.position.x / 100) * 800;
                    const endY = (nextRegion.position.y / 100) * 600;

                    return (
                      <line
                        key={`${regionId}-${nextRegionId}`}
                        x1={startX}
                        y1={startY}
                        x2={endX}
                        y2={endY}
                        stroke="rgba(98, 125, 152, 0.4)"
                        strokeWidth="3"
                        strokeDasharray="10,5"
                        strokeLinecap="round"
                      />
                    );
                  })}
                </svg>
              )}
            </div>

            {/* Legend */}
            <div className="absolute bottom-4 left-4 bg-[var(--bg-raised)] bg-opacity-95 p-4 border-2 border-charcoal-200 max-w-xs">
              <h3 className="font-bold text-charcoal-900 mb-2 flex items-center gap-2">
                <Compass className="h-4 w-4" />
                Island Legend
              </h3>
              <div className="space-y-1 text-xs text-charcoal-700">
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-consulting-slate-blue-100 border-2 border-consulting-slate-blue-400 rounded-full" />
                  <span>Discovered Region</span>
                </div>
                <div className="flex items-center gap-2">
                  <div className="w-4 h-4 bg-sage-100 border-2 border-sage-400 rounded-full opacity-75" />
                  <span>Undiscovered Region</span>
                </div>
                <div className="flex items-center gap-2">
                  <Sparkles className="h-4 w-4 text-[var(--mustard)]" />
                  <span>New Discovery!</span>
                </div>
              </div>
            </div>
          </div>
        </div>
      )}

      {/* Selected Region Detail */}
      {selectedRegionData && (
        <div className={`${selectedRegionData.bgColor}  border-2 ${selectedRegionData.borderColor} overflow-hidden shadow-xl transform transition-all`}>
          <div className="p-8">
            <div className="flex items-start gap-4 mb-6">
              <div className={`${selectedRegionData.bgColor} p-4  border-2 ${selectedRegionData.borderColor}`}>
                {React.createElement(selectedRegionData.icon, {
                  className: `h-12 w-12 ${selectedRegionData.color}`
                })}
              </div>
              <div className="flex-1">
                <div className="flex items-center gap-3 mb-2">
                  <h2 className="text-3xl font-bold text-charcoal-900">{selectedRegionData.name}</h2>
                  {visitedRegions.has(selectedRegionData.id) && (
                    <span className="px-3 py-1 bg-[var(--mustard-dim)] text-[var(--mustard)] rounded-full text-sm font-medium flex items-center gap-1">
                      <Star className="h-3 w-3" />
                      Discovered
                    </span>
                  )}
                </div>
                <p className={`text-xl ${selectedRegionData.color} font-medium mb-3`}>
                  {selectedRegionData.tagline}
                </p>
                <p className="text-charcoal-700 leading-relaxed">
                  {selectedRegionData.description}
                </p>
              </div>
            </div>

            {/* Terrain Info */}
            <div className="bg-[var(--bg-raised)] bg-opacity-60 p-4 mb-6 border border-charcoal-200">
              <div className="flex items-center gap-2 mb-2">
                <MapIcon className="h-5 w-5 text-charcoal-600" />
                <h3 className="font-bold text-charcoal-900">Terrain</h3>
              </div>
              <p className="text-sm text-charcoal-700">{selectedRegionData.terrain}</p>
            </div>

            {/* Capabilities */}
            <div className="mb-6">
              <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                <Zap className="h-5 w-5 text-[var(--mustard)]" />
                Capabilities to Master
              </h3>
              <div className="grid grid-cols-3 gap-3">
                {selectedRegionData.capabilities.map((cap, idx) => (
                  <div key={idx} className="bg-[var(--bg-raised)] p-3 border border-charcoal-200 text-center">
                    <p className="text-sm font-medium text-charcoal-800">{cap}</p>
                  </div>
                ))}
              </div>
            </div>

            {/* Frameworks */}
            <div>
              <h3 className="font-bold text-charcoal-900 mb-3 flex items-center gap-2">
                <BookOpen className="h-5 w-5 text-consulting-slate-blue-600" />
                Available Frameworks & Tools
              </h3>
              <div className="space-y-2">
                {selectedRegionData.frameworks.map((framework, idx) => (
                  <Link
                    key={idx}
                    to={`/elevation/entry/${framework.id}`}
                    className="block bg-[var(--bg-raised)] hover:bg-consulting-slate-blue-50 p-4 border border-charcoal-200 hover:border-consulting-slate-blue-400 transition-all group"
                  >
                    <div className="flex items-center justify-between">
                      <div className="flex items-center gap-3">
                        <Rocket className="h-5 w-5 text-consulting-slate-blue-600" />
                        <span className="font-medium text-charcoal-900">{framework.name}</span>
                      </div>
                      <ArrowRight className="h-5 w-5 text-consulting-slate-blue-600 group-hover:translate-x-1 transition-transform" />
                    </div>
                  </Link>
                ))}
              </div>
            </div>

            {/* Call to Action */}
            <div className="mt-6 bg-[var(--bg-raised)] p-4 border-2 border-consulting-slate-blue-300">
              <p className="text-sm text-charcoal-700 flex items-start gap-2">
                <Sparkles className="h-5 w-5 text-[var(--mustard)] flex-shrink-0 mt-0.5" />
                <span>
                  <strong>Explorer's Tip:</strong> Visit each framework to gain deeper knowledge. 
                  The more regions you explore, the more connections you'll discover between different capabilities.
                </span>
              </p>
            </div>
          </div>
        </div>
      )}

      {/* All Regions Grid (when no region selected) */}
      {!selectedRegion && (
        <div className="bg-[var(--bg-raised)] border-2 border-charcoal-200 p-6">
          <h2 className="text-2xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
            <MapIcon className="h-6 w-6 text-consulting-slate-blue-600" />
            All Regions
          </h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-4">
            {regions.map((region) => {
              const Icon = region.icon;
              const isVisited = visitedRegions.has(region.id);
              
              return (
                <button
                  key={region.id}
                  onClick={() => handleRegionClick(region.id)}
                  className={`
                    ${region.bgColor} ${region.borderColor}
                     border-2 p-4 text-left
                    hover:shadow-lg transition-all
                    ${isVisited ? 'opacity-100' : 'opacity-75'}
                  `}
                >
                  <div className="flex items-start gap-3">
                    <Icon className={`${region.color} h-8 w-8 flex-shrink-0`} />
                    <div className="flex-1 min-w-0">
                      <div className="flex items-center gap-2 mb-1">
                        <h3 className="font-bold text-charcoal-900 truncate">{region.name}</h3>
                        {isVisited && <Star className="h-4 w-4 text-[var(--mustard)] flex-shrink-0" />}
                      </div>
                      <p className={`text-sm ${region.color} font-medium mb-2`}>
                        {region.tagline}
                      </p>
                      <p className="text-xs text-charcoal-600 line-clamp-2">
                        {region.description}
                      </p>
                    </div>
                  </div>
                  <div className="mt-3 flex items-center gap-1 text-xs text-charcoal-600">
                    <ChevronRight className="h-3 w-3" />
                    <span>{region.frameworks.length} framework{region.frameworks.length !== 1 ? 's' : ''} available</span>
                  </div>
                </button>
              );
            })}
          </div>
        </div>
      )}

      {/* Journey Guide */}
      <div className="bg-gradient-to-r from-sage-50 to-consulting-slate-blue-50 border-2 border-sage-200 p-6">
        <h2 className="text-xl font-bold text-charcoal-900 mb-4 flex items-center gap-2">
          <Compass className="h-6 w-6 text-sage-600" />
          Your Journey to Mastery
        </h2>
        <div className="grid md:grid-cols-3 gap-4 text-sm">
          <div className="bg-[var(--bg-raised)] p-4 border border-sage-200">
            <h3 className="font-bold text-sage-900 mb-2 flex items-center gap-2">
              <MapIcon className="h-4 w-4" />
              Explore
            </h3>
            <p className="text-charcoal-700">
              Click on regions to discover new capabilities and frameworks. Each area offers unique insights.
            </p>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-consulting-slate-blue-200">
            <h3 className="font-bold text-consulting-slate-blue-900 mb-2 flex items-center gap-2">
              <BookOpen className="h-4 w-4" />
              Learn
            </h3>
            <p className="text-charcoal-700">
              Access frameworks and tools in each region. Study the M3 encodings to understand deep structure.
            </p>
          </div>
          <div className="bg-[var(--bg-raised)] p-4 border border-[var(--mustard)]">
            <h3 className="font-bold text-[var(--mustard)] mb-2 flex items-center gap-2">
              <Rocket className="h-4 w-4" />
              Apply
            </h3>
            <p className="text-charcoal-700">
              Use templates and tools to transform knowledge into action. Build your prioritization mastery.
            </p>
          </div>
        </div>
      </div>
    </div>
  );
};
